import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The clock a race is played back on (SPEC-008 PR-8.5).
 *
 * The one module in this vertical that touches `requestAnimationFrame`, and it
 * knows nothing about cars: hand it a duration and it counts simulated seconds
 * up to it, once, and stops. That split is the same one `cardGesture.js` and
 * `useCardGestures.js` are drawn along — the arithmetic of the race is pure and
 * lives in `domain/race`, and the browser's frame loop lives here.
 *
 * It returns SIMULATED seconds rather than wall-clock ones, which is what makes
 * the compression in D-18 invisible to every caller. A forty-second snow climb
 * plays in twelve and the clock still reads forty, because the clock is reading
 * the race and not the reader's watch.
 *
 * It also HOLDS. Given a sorted list of simulated marks it stops at each one for
 * a moment and then goes on, and the simulated time it spends stopped is zero —
 * a hold is real seconds off the reader's watch and no seconds off the race, so
 * a frozen strip cannot drift out of step with the trace under it. That is what
 * lets the screen stop and explain a wheelspin without the clock beside it
 * counting through the explanation.
 *
 * `now` and `schedule` are defaulted parameters rather than globals reached for
 * inside, per the repo's browser-API rule: a hook that called
 * `requestAnimationFrame` directly could not be told to advance in a test, and
 * jsdom does not run frames on its own.
 */

/**
 * The longest a race is allowed to take in the reader's own time.
 *
 * A snow climb is the better part of a minute and a minute of watching two dots
 * is not a feature. Above this the strip plays at a multiple and says so — the
 * clock still counts simulated seconds, which is exactly the arrangement a
 * racing game has always used and the reason it is legible without explanation.
 */
export const PLAYBACK_CAP_SECONDS = 12;

/** Below this a race plays in real time, because real time is the honest speed. */
export function playbackRate(duration) {
    return duration > PLAYBACK_CAP_SECONDS ? duration / PLAYBACK_CAP_SECONDS : 1;
}

/**
 * How long the strip stops at a moment, in the reader's own seconds.
 *
 * Long enough to read two short lines and look at where the cars are, short
 * enough that three of them do not turn a twelve-second race into a slideshow.
 * It is wall-clock rather than simulated, deliberately: the reading time a
 * sentence needs has nothing to do with how fast the cars are going.
 */
export const HOLD_MILLISECONDS = 1700;

const defaultNow = () => (typeof performance === 'undefined' ? Date.now() : performance.now());
const defaultSchedule = (callback) => (
    typeof requestAnimationFrame === 'undefined' ? null : requestAnimationFrame(callback)
);
const defaultCancel = (handle) => {
    if (typeof cancelAnimationFrame !== 'undefined' && handle !== null) {
        cancelAnimationFrame(handle);
    }
};

/**
 * Count from zero to `duration` in simulated seconds.
 *
 * `status` is the whole state and there are only three of it: `idle` before the
 * reader has asked for anything, `running` while the frames are arriving, and
 * `finished` once the clock has reached the end. A boolean pair would allow a
 * fourth combination that means nothing.
 *
 * `skip` is not a nicety. The race is the one thing on this screen that takes
 * time, and a reader who has changed the surface and wants the number should
 * never have to wait for a car to arrive — so the same control that runs it
 * finishes it, holds and all.
 */
export default function useRaceClock(duration, {
    holds = [],
    reducedMotion = false,
    now = defaultNow,
    schedule = defaultSchedule,
    cancel = defaultCancel,
} = {}) {
    const [status, setStatus] = useState('idle');
    const [seconds, setSeconds] = useState(0);
    // Which hold the strip is stopped at, by index, or null while it is moving.
    // An index rather than the mark itself, so the caller can look the moment up
    // in the list it handed over instead of matching on a float.
    const [holdIndex, setHoldIndex] = useState(null);
    const frame = useRef(null);
    const startedAt = useRef(0);
    // Real milliseconds spent stopped. Subtracted from the elapsed wall clock,
    // which is what makes a hold cost the reader time and the race none.
    const heldFor = useRef(0);
    const resumeAt = useRef(0);
    const nextHold = useRef(0);

    const stop = useCallback(() => {
        cancel(frame.current);
        frame.current = null;
    }, [cancel]);

    const finish = useCallback(() => {
        stop();
        setSeconds(duration);
        setHoldIndex(null);
        setStatus('finished');
    }, [duration, stop]);

    const start = useCallback(() => {
        stop();

        // Under reduced motion the race is RUN AND STATED rather than animated
        // (D-19). The result and both traces are the content; the animation is
        // one way of delivering it, and the splash makes the same trade for the
        // same reason (SPEC-001).
        if (reducedMotion || !(duration > 0)) {
            finish();

            return;
        }

        const rate = playbackRate(duration);

        startedAt.current = now();
        heldFor.current = 0;
        resumeAt.current = 0;
        nextHold.current = 0;
        setSeconds(0);
        setHoldIndex(null);
        setStatus('running');

        const step = () => {
            const moment = now();

            // Stopped at a moment: the wall clock runs on, the race does not.
            if (resumeAt.current > 0) {
                if (moment < resumeAt.current) {
                    frame.current = schedule(step);

                    return;
                }

                heldFor.current += moment - (resumeAt.current - HOLD_MILLISECONDS);
                resumeAt.current = 0;
                setHoldIndex(null);
            }

            const elapsed = ((moment - startedAt.current - heldFor.current) / 1000) * rate;
            const upcoming = holds[nextHold.current];

            if (upcoming !== undefined && elapsed >= upcoming) {
                // Frozen ON the mark rather than wherever the frame happened to
                // land, so the sentence and the picture describe the same
                // instant — a frame can be twenty milliseconds late and at
                // playback speed that is a car length.
                setSeconds(upcoming);
                setHoldIndex(nextHold.current);
                nextHold.current += 1;
                resumeAt.current = moment + HOLD_MILLISECONDS;
                frame.current = schedule(step);

                return;
            }

            if (elapsed >= duration) {
                finish();

                return;
            }

            setSeconds(elapsed);
            frame.current = schedule(step);
        };

        frame.current = schedule(step);
    }, [duration, finish, holds, now, reducedMotion, schedule, stop]);

    // A new race — a different pair, a different surface — invalidates the one
    // on screen. Resetting to `idle` rather than replaying it is deliberate:
    // changing the road under a running car would show a result the reader never
    // asked for, and the strip that is already drawn is the previous question's
    // answer.
    useEffect(() => {
        stop();
        setStatus('idle');
        setSeconds(0);
        setHoldIndex(null);
    }, [duration, stop]);

    useEffect(() => stop, [stop]);

    return {
        seconds,
        status,
        // Which moment the strip is stopped at, or null. The caller owns the
        // list; this only ever says where in it the clock is.
        holdIndex,
        rate: playbackRate(duration),
        start,
        skip: finish,
    };
}
